import React, {
    useState
} from 'react';
import {
    Popover,
    PopoverTrigger,
    PopoverContent
} from 'shared/components/ui/popover';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Slider
} from 'shared/components/ui/slider';
import {
    Button
} from 'shared/components/ui/button';
import {
    Settings2,
    Info
} from 'lucide-react';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from 'shared/components/ui/tooltip';

interface DefaultConfigurationParams {
    temperature: number;
    max_tokens: number;
    top_p: number;
    stop: string | string[];
    frequency_penalty: number;
    presence_penalty: number;
    seed?: number;
    stream: boolean;
}

interface DefaultConfigurationProps {
    parameters: DefaultConfigurationParams;
    setParameters: (params: DefaultConfigurationParams) => void;
}

const DefaultConfiguration: React.FC<DefaultConfigurationProps> = ({
    parameters,
    setParameters
}) => {
    const [tempLimit] = useState({
        min: 0,
        max: 2
    });
    const [topPLimit] = useState({
        min: 0,
        max: 1
    });
    const [frequencyPenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [presencePenaltyLimit] = useState({
        min: -2.0,
        max: 2.0
    });
    const [seedLimit] = useState({
        min: -2147483648, // -2^31
        max: 2147483647 // 2^31 - 1
    });

    const handleStopChange = (value: string) => {
        // Convert comma-separated string to array or keep as single string
        if (value.includes(',')) {
            setParameters({
                ...parameters,
                stop: value.split(',').map(s => s.trim()).filter(s => s)
            });
        } else {
            setParameters({
                ...parameters,
                stop: value
            });
        }
    };

    const getStopValue = (): string => {
        if (Array.isArray(parameters.stop)) {
            return parameters.stop.join(', ');
        }
        return parameters.stop || '';
    };

    const InfoIcon: React.FC<{ content: string }> = ({
        content
    }) => (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                    <p className="text-xs">{content}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );

    const renderFieldWithSlider = (
        id: string,
        label: string,
        tooltip: string,
        value: number,
        onChange: (val: number) => void,
        min: number,
        max: number,
        step: number
    ) => (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Label htmlFor={id} className="text-sm">{label}</Label>
                    <InfoIcon content={tooltip} />
                </div>
                <Input
                    id={id}
                    type="number"
                    value={value}
                    step={step}
                    max={max}
                    min={min}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="h-8 w-20"
                />
            </div>
            <Slider
                id={`${id}-slider`}
                value={[value]}
                onValueChange={(val) => onChange(Number(val))}
                step={step}
                max={max}
                min={min}
                className="w-full"
            />
        </div>
    );

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div className=''>
                    <Button aria-haspopup="true" size="icon" variant="ghost">
                        <Settings2 className="h-5 w-5" />
                    </Button>
                </div>
            </PopoverTrigger>

            <PopoverContent className="w-[680px] select-none max-h-[600px] overflow-y-auto" align="center">
                <div className="grid gap-4">
                    <div className="space-y-2">
                        <h4 className="font-medium leading-none text-sm">Configure Model Parameters</h4>
                        <p className="text-xs text-muted-foreground">
                            Set the parameters for the selected model.
                        </p>
                    </div>

                    {/* Two Column Layout */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* Column 1 */}
                        <div className="space-y-4">
                            {/* Max Tokens */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="maxTokens" className="text-sm">Max Tokens</Label>
                                    <InfoIcon content="Maximum number of tokens to generate. Controls the length of the output. Higher values allow longer responses." />
                                </div>
                                <Input
                                    id="maxTokens"
                                    type="number"
                                    value={parameters.max_tokens}
                                    step={1}
                                    min={1}
                                    onChange={(e) => {
                                        setParameters({
                                            ...parameters,
                                            max_tokens: Number(e.target.value)
                                        });
                                    }}
                                    className="h-8"
                                />
                            </div>

                            {/* Temperature */}
                            {renderFieldWithSlider(
                                'temp',
                                'Temperature',
                                'Controls randomness in output. Lower values (e.g., 0.2) make output more focused and deterministic. Higher values (e.g., 1.5) make it more creative and diverse.',
                                parameters.temperature,
                                (val) => setParameters({
                                    ...parameters,
                                    temperature: val
                                }),
                                tempLimit.min,
                                tempLimit.max,
                                0.1
                            )}

                            {/* Top P */}
                            {renderFieldWithSlider(
                                'topP',
                                'Top P',
                                'Nucleus sampling. Controls diversity by limiting token selection to cumulative probability. Lower values (e.g., 0.1) make output more focused, higher values (e.g., 0.9) more diverse.',
                                parameters.top_p,
                                (val) => setParameters({
                                    ...parameters,
                                    top_p: val
                                }),
                                topPLimit.min,
                                topPLimit.max,
                                0.1
                            )}

                            {/* Frequency Penalty */}
                            {renderFieldWithSlider(
                                'frequencyPenalty',
                                'Frequency Penalty',
                                'Reduces repetition by penalizing tokens based on how often they appear. Positive values discourage repetition, negative values encourage it.',
                                parameters.frequency_penalty,
                                (val) => setParameters({
                                    ...parameters,
                                    frequency_penalty: val
                                }),
                                frequencyPenaltyLimit.min,
                                frequencyPenaltyLimit.max,
                                0.1
                            )}
                        </div>

                        {/* Column 2 */}
                        <div className="space-y-4">
                            {/* Presence Penalty */}
                            {renderFieldWithSlider(
                                'presencePenalty',
                                'Presence Penalty',
                                'Encourages the model to talk about new topics by penalizing tokens that have already appeared. Positive values increase topic diversity.',
                                parameters.presence_penalty,
                                (val) => setParameters({
                                    ...parameters,
                                    presence_penalty: val
                                }),
                                presencePenaltyLimit.min,
                                presencePenaltyLimit.max,
                                0.1
                            )}

                            {/* Stop Sequences */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="stop" className="text-sm">Stop Sequences</Label>
                                    <InfoIcon content="Sequences where the model will stop generating. Useful for controlling output format. Enter multiple sequences separated by commas (e.g., '\n\n', '</END>')." />
                                </div>
                                <Input
                                    id="stop"
                                    type="text"
                                    value={getStopValue()}
                                    onChange={(e) => handleStopChange(e.target.value)}
                                    placeholder="e.g., \n\n, </END>"
                                    className="h-8"
                                />
                            </div>

                            {/* Seed */}
                            <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor="seed" className="text-sm">Seed (optional)</Label>
                                    <InfoIcon content="Sets a random seed for deterministic output. Using the same seed with identical inputs will produce the same output. Leave empty for random generation." />
                                </div>
                                <Input
                                    id="seed"
                                    type="number"
                                    value={parameters.seed || ''}
                                    step={1}
                                    max={seedLimit.max}
                                    min={seedLimit.min}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setParameters({
                                            ...parameters,
                                            seed: value ? Number(value) : undefined
                                        });
                                    }}
                                    placeholder="None"
                                    className="h-8"
                                />
                            </div>

                            {/* Stream */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <Label htmlFor="stream" className="text-sm">Stream Output</Label>
                                        <InfoIcon content="Enable token-by-token streaming. When enabled, the model generates and sends output progressively instead of waiting for completion." />
                                    </div>
                                    <Switch
                                        id="stream"
                                        checked={parameters.stream}
                                        onCheckedChange={(checked) => {
                                            setParameters({
                                                ...parameters,
                                                stream: checked
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default DefaultConfiguration;
